import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import { errorText } from '@/lib/apiErrors';
import { useFeatureAccess } from '@/contexts/FeatureAccessContext';
import { OpenConversationProvider } from '@/contexts/OpenConversationContext';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import FeedbackWidget from '@/components/FeedbackWidget';
import { ConsentBanner } from '@/components/ConsentBanner';
import { LoginConsentPrompt } from '@/components/LoginConsentPrompt';
import { Heart, Home, Pill, FileText, Users, MessageCircle, CreditCard, User, Bell, LogOut, Menu, X, Calendar, BellRing, Clock, Check, ChevronRight, Activity, Video, Building2, BarChart3, Star, Briefcase, Shield, Headphones, ShieldCheck, Gift, Stethoscope, FileBarChart, Brain, ClipboardList, TestTube2, Salad, Sparkles, Calculator } from 'lucide-react';
import { NsofarLogo } from './NsofarLogo';
import { SEOHead } from './SEOHead';
import { CurrencySwitcher } from './CurrencySwitcher';
import TrustChip from './TrustChip';
import DarkModeToggle from './DarkModeToggle';

const userNavItems = [
  { path: '/', label: 'Dashboard', icon: Home },
  { path: '/ai-chat', label: 'AI Assistant', icon: Sparkles, isNew: true },
  { path: '/medicine-delivery', label: 'Medicine Delivery', icon: Pill, featureKey: 'medicine_delivery_v01' },
  { path: '/carer-request', label: 'Carer Services', icon: Users, featureKey: 'carer_v01' },
  { path: '/diagnostics', label: 'Diagnostics', icon: TestTube2, featureKey: 'diagnostics_v01' },
  { path: '/property-request', label: 'Property Management', icon: Building2, featureKey: 'property_v01' },
  { path: '/nutritionist', label: 'Nutritionist', icon: Salad, featureKey: 'nutritionist_v01' },
  { path: '/my-requests', label: 'My Requests', icon: ClipboardList },
  { path: '/health-trends', label: 'Health Trends', icon: Activity, featureKey: 'health_trends' },
  { path: '/medications', label: 'Medications', icon: Pill, featureKey: 'medications' },
  { path: '/reports', label: 'Reports', icon: FileText, featureKey: 'health_reports' },
  { path: '/appointments', label: 'Appointments', icon: Calendar, featureKey: 'appointments' },
  { path: '/consultations', label: 'Consultations', icon: Video, featureKey: 'doctor_consultation_v01' },
  { path: '/carers', label: 'Carer Network', icon: Users, featureKey: 'carer_network_integration' },
  { path: '/properties', label: 'Properties', icon: Building2, featureKey: 'property_integration' },
  { path: '/insurance', label: 'Insurance', icon: Shield, featureKey: 'insurance' },
  { path: '/taxes', label: 'Tax Filing', icon: Calculator, featureKey: 'taxes', isNew: true },
  { path: '/chat', label: 'Get Help', icon: Headphones, featureKey: 'chat' },
  { path: '/plans', label: 'Plans', icon: CreditCard, featureKey: 'plans', showUnlessDisabled: true },
  { path: '/billing', label: 'Billing', icon: CreditCard },
  { path: '/referrals', label: 'Referrals', icon: Gift },
  { path: '/health-graph', label: 'Health Graph', icon: Activity, featureKey: 'health_graph' },
  { path: '/health-passport', label: 'Health Passport', icon: Stethoscope, featureKey: 'health_passport' },
  { path: '/monthly-reports', label: 'Monthly Report', icon: FileBarChart, featureKey: 'monthly_reports' },
  { path: '/settings/privacy', label: 'Privacy', icon: ShieldCheck },
  { path: '/profile', label: 'Profile', icon: User },
  { path: '/data-control', label: 'Your Data', icon: ShieldCheck },
];

const carerNavItems = [
  { path: '/carer-admin', label: 'Dashboard', icon: Home },
  { path: '/profile', label: 'Profile', icon: User },
];

const adminNavItems = [
  { path: '/admin', label: 'Admin', icon: Shield },
  { path: '/admin/consults', label: 'Doctor Consult Ops', icon: Stethoscope },
  { path: '/ai-training', label: 'AI Training', icon: Brain },
  { path: '/dashboard', label: 'User Dashboard', icon: Home },
  { path: '/ai-chat', label: 'AI Assistant', icon: Sparkles, isNew: true },
  { path: '/medicine-delivery', label: 'Medicine Delivery', icon: Pill, featureKey: 'medicine_delivery_v01' },
  { path: '/carer-request', label: 'Carer Services', icon: Users, featureKey: 'carer_v01' },
  { path: '/diagnostics', label: 'Diagnostics', icon: TestTube2, featureKey: 'diagnostics_v01' },
  { path: '/property-request', label: 'Property Management', icon: Building2, featureKey: 'property_v01' },
  { path: '/nutritionist', label: 'Nutritionist', icon: Salad, featureKey: 'nutritionist_v01' },
  { path: '/my-requests', label: 'My Requests', icon: ClipboardList },
  { path: '/health-trends', label: 'Health Trends', icon: Activity, featureKey: 'health_trends' },
  { path: '/medications', label: 'Medications', icon: Pill, featureKey: 'medications' },
  { path: '/reports', label: 'Reports', icon: FileText, featureKey: 'health_reports' },
  { path: '/appointments', label: 'Appointments', icon: Calendar, featureKey: 'appointments' },
  { path: '/consultations', label: 'Consultations', icon: Video, featureKey: 'doctor_consultation_v01' },
  { path: '/carers', label: 'Carer Network', icon: Users, featureKey: 'carer_network_integration' },
  { path: '/properties', label: 'Properties', icon: Building2, featureKey: 'property_integration' },
  { path: '/insurance', label: 'Insurance', icon: Shield, featureKey: 'insurance' },
  { path: '/taxes', label: 'Tax Filing', icon: Calculator, featureKey: 'taxes', isNew: true },
  { path: '/chat', label: 'Get Help', icon: Headphones, featureKey: 'chat' },
  { path: '/plans', label: 'Plans', icon: CreditCard, featureKey: 'plans', showUnlessDisabled: true },
  { path: '/billing', label: 'Billing', icon: CreditCard },
  { path: '/health-graph', label: 'Health Graph', icon: Activity, featureKey: 'health_graph' },
  { path: '/health-passport', label: 'Health Passport', icon: Stethoscope, featureKey: 'health_passport' },
  { path: '/monthly-reports', label: 'Monthly Report', icon: FileBarChart, featureKey: 'monthly_reports' },
  { path: '/settings/privacy', label: 'Privacy', icon: ShieldCheck },
  { path: '/profile', label: 'Profile', icon: User },
];

const csNavItems = [
  { path: '/cs', label: 'CS Dashboard', icon: Headphones },
  { path: '/profile', label: 'Profile', icon: User },
];

const pmNavItems = [
  { path: '/pm', label: 'Properties', icon: Building2 },
  { path: '/profile', label: 'Profile', icon: User },
];

// Mobile bottom nav (Session 7 / B1). User-role only; mobile-first per
// P1. Long-tail items (Medications / Reports / Insurance / ...) stay
// reachable via the hamburger drawer that hangs off the top bar. Plans is
// the upgrade destination: shown unless the admin disables its
// "Subscription Plans" feature toggle — Min Plan / Coming Soon never hide
// it (showUnlessDisabled), so under-tier users can always reach /plans.
const bottomNavItems = [
  { path: '/',            label: 'Home',     icon: Home },
  { path: '/my-requests', label: 'Requests', icon: ClipboardList },
  { path: '/plans',       label: 'Plans',    icon: CreditCard, featureKey: 'plans', showUnlessDisabled: true },
  { path: '/profile',     label: 'Profile',  icon: User },
];

function isNewFeature(userCreatedAt) {
  if (!userCreatedAt) return true; // show for users without created_at
  const created = new Date(userCreatedAt);
  const fourteenDaysAgo = new Date(Date.now() - 14 * 24 * 60 * 60 * 1000);
  return created > fourteenDaysAgo;
}

export default function Layout({ children }) {
  const { user, api, logout } = useAuth();
  const { canAccess, loaded } = useFeatureAccess();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [notifOpen, setNotifOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [notifPermission, setNotifPermission] = useState('default');
  const [referralEnabled, setReferralEnabled] = useState(true);
  const notifRef = useRef(null);
  // Set by the page through OpenConversationContext: /chat with a
  // conversation open, not its list.
  const [conversationOpen, setConversationOpen] = useState(false);
  // The query index.css fits the chat pages to a phone screen with
  // (`chat-fit`): what Tailwind's `lg:` is the other side of.
  const belowLg = useMediaQuery('not all and (min-width: 1024px)');

  const currentPath = location.pathname;
  const isCarer = user?.role === 'carer';
  const isAdmin = user?.role === 'admin';
  const isCS = user?.role === 'customer_service';
  const isPM = user?.role === 'property_manager';
  const isUser = user?.role === 'user';
  const navItems = isAdmin ? adminNavItems : isCarer ? carerNavItems : isCS ? csNavItems : isPM ? pmNavItems : userNavItems;
  // The assistant button: NRI portal only, not on the assistant's own page,
  // not when chat is switched off for the account. Below lg it is left out
  // of an open conversation on /chat too: that page is one screen tall
  // there and its composer keeps room for the Feedback trigger alone, as
  // on /ai-chat (the button would lie on Send). The conversation list has
  // it, and from lg up nothing changes. One name for it, because the
  // Feedback trigger is placed by whether the button is there: never hide
  // the button with a class instead.
  const showAssistantFab = isUser && currentPath !== '/ai-chat' && !(belowLg && conversationOpen) && (!loaded || canAccess('chat')?.accessible !== false);

  // Filter nav items based on feature toggles and referral status
  // Empty until loaded — never flash then hide
  const filteredNavItems = loaded
    ? navItems.filter(item => {
        if (item.path === '/referrals' && !referralEnabled) return false;
        if (item.featureKey) {
          const access = canAccess(item.featureKey);
          if (access && access.accessible === false) {
            // Plans (showUnlessDisabled) is the upgrade destination — keep it
            // visible when it's merely plan-gated or "coming soon"; only an
            // explicit admin disable (or per-user disable) hides it.
            if (item.showUnlessDisabled) {
              if (access.reason === 'feature_disabled' || access.reason === 'disabled_for_user') return false;
            } else {
              return false;
            }
          }
        }
        return true;
      })
    : [];

  // Check referral program status
  useEffect(() => {
    if (api && isUser) {
      api.get('/referrals/my-code').then(r => {
        setReferralEnabled(r.data.referral_enabled !== false);
      }).catch(() => {});
    }
  }, [api, isUser]);

  // Fetch notifications and generate new ones
  const fetchNotifications = useCallback(async () => {
    try {
      await api.post('/notifications/generate');
      const [notifRes, countRes] = await Promise.all([
        api.get('/notifications'),
        api.get('/notifications/unread-count')
      ]);
      setNotifications(notifRes.data.filter(n => !n.dismissed).slice(0, 20));
      const newCount = countRes.data.count;

      // Browser push notification for new items
      if (newCount > unreadCount && notifPermission === 'granted') {
        const newest = notifRes.data.find(n => !n.read && !n.dismissed);
        if (newest && 'Notification' in window) {
          new window.Notification(newest.title, {
            body: newest.body,
            icon: '/favicon.ico',
            tag: newest.id,
          });
        }
      }
      setUnreadCount(newCount);
    } catch {}
  }, [api, unreadCount, notifPermission]);

  // Request browser notification permission
  useEffect(() => {
    if (!('Notification' in window)) {
      setNotifPermission('unsupported');
    } else {
      setNotifPermission(window.Notification.permission);
    }
  }, []);

  // Poll notifications every 30 seconds
  useEffect(() => {
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 30000);
    return () => clearInterval(interval);
  }, [fetchNotifications]);

  // Close dropdown on outside click
  useEffect(() => {
    const handler = (e) => {
      if (notifRef.current && !notifRef.current.contains(e.target)) setNotifOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const requestNotifPermission = async () => {
    if (!('Notification' in window)) {
      setNotifPermission('unsupported');
      return;
    }
    try {
      const perm = await window.Notification.requestPermission();
      setNotifPermission(perm);
      if (perm === 'granted') {
        new window.Notification('nsofar', { body: 'Push notifications enabled!', icon: '/favicon.ico' });
      }
    } catch {
      setNotifPermission('unsupported');
    }
  };

  const markAllRead = async () => {
    try {
      await api.post('/notifications/mark-all-read');
      setUnreadCount(0);
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
    } catch {}
  };

  const dismissNotif = async (id) => {
    try {
      await api.post(`/notifications/dismiss/${id}`);
      setNotifications(prev => prev.filter(n => n.id !== id));
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (err) {
      toast.error(errorText(err, 'Could not dismiss the notification. Please try again.'));
    }
  };

  const getNotifIcon = (type) => {
    if (type?.includes('medication_overdue')) return { icon: Pill, bg: 'var(--error-light)', color: 'var(--error)' };
    if (type?.includes('medication_due')) return { icon: Pill, bg: 'var(--warning-light)', color: 'var(--warning)' };
    if (type?.includes('medication')) return { icon: Pill, bg: 'var(--brand-primary-light)', color: 'var(--brand-primary)' };
    if (type?.includes('appointment')) return { icon: Calendar, bg: 'var(--brand-secondary-light)', color: 'var(--brand-secondary)' };
    return { icon: Bell, bg: 'var(--brand-primary-light)', color: 'var(--brand-primary)' };
  };

  return (
    // `clip-x`, not `overflow-x-hidden`: the top bar below is `sticky`, and
    // under an `overflow-x-hidden` box it sticks to that box, not to the
    // screen (see `.clip-x` in index.css).
    <div className="min-h-screen flex clip-x" style={{ background: 'var(--bg-main)' }}>
      {/* The signed-in app is never a search result. */}
      <SEOHead noindex />
      {/* Sidebar - Desktop */}
      <aside className="hidden lg:flex flex-col w-64 border-r bg-white fixed h-screen z-30" style={{ borderColor: 'var(--border-color)' }}>
        <div className="p-6 border-b" style={{ borderColor: 'var(--border-color)' }}>
          <div className="cursor-pointer" onClick={() => navigate('/')}>
            <NsofarLogo size="md" />
          </div>
        </div>
        <nav className="flex-1 px-3 py-4 space-y-0.5 overflow-y-auto" data-tour="sidebar-nav">
          {filteredNavItems.filter(item => {
            if (!item.featureKey) return true;
            const access = canAccess(item.featureKey);
            return access.reason !== 'feature_disabled' && access.reason !== 'disabled_for_user';
          }).map(item => {
            const active = currentPath === item.path;
            return (
              <button key={item.path} data-testid={`nav-${item.label.toLowerCase()}`}
                onClick={() => navigate(item.path)}
                className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-all duration-200 relative"
                style={{
                  background: active ? 'var(--brand-primary-light)' : 'transparent',
                  color: active ? 'var(--brand-primary)' : 'var(--text-muted)',
                  fontWeight: active ? 600 : 500,
                }}>
                {active && <span className="absolute left-0 top-1/2 -translate-y-1/2 w-[3px] h-5 rounded-r-full" style={{ background: 'var(--brand-primary)' }} />}
                <item.icon size={18} style={{ opacity: active ? 1 : 0.7 }} /> {item.label}
                {item.isNew && isNewFeature(user?.created_at) && (
                  <span className="ml-auto px-1.5 py-0.5 rounded text-[11px] font-bold text-white" style={{ background: 'var(--brand-primary)' }}>New</span>
                )}
              </button>
            );
          })}
        </nav>
        <div className="p-4 border-t" style={{ borderColor: 'var(--border-color)' }}>
          <div className="flex items-center gap-3 mb-3 px-2">
            <div className="w-9 h-9 rounded-full flex items-center justify-center text-white text-sm font-semibold shadow-sm" style={{ background: 'linear-gradient(135deg, var(--brand-primary), var(--brand-primary-hover))' }}>
              {user?.full_name?.charAt(0) || 'U'}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold truncate nsofar-pii" style={{ color: 'var(--text-main)' }}>{user?.full_name}</p>
              <p className="text-xs truncate nsofar-pii" style={{ color: 'var(--text-light)' }}>{user?.email}</p>
            </div>
          </div>
          <button data-testid="logout-btn" onClick={logout}
            className="w-full flex items-center gap-2 px-3 py-2 rounded-xl text-sm transition-colors hover:bg-[var(--error-light)]" style={{ color: 'var(--error)' }}>
            <LogOut size={16} /> Sign Out
          </button>
        </div>
      </aside>

      {/* Mobile overlay. z-[45]: over everything the frame keeps on screen
          (header 20, sidebar 30, assistant button, bottom nav and Feedback
          trigger 40) and under a page's dialog (50). At z-40 it tied with the
          last three, which come later in this file and so were drawn on top
          of it: the bottom nav lay on Sign Out. */}
      {sidebarOpen && (
        <div className="lg:hidden fixed inset-0 bg-black/30 z-[45]" onClick={() => setSidebarOpen(false)} data-testid="mobile-menu">
          <aside className="w-72 h-full bg-white animate-slide-right flex flex-col" onClick={e => e.stopPropagation()}>
            <div className="p-4 flex items-center justify-between border-b" style={{ borderColor: 'var(--border-color)' }}>
              <div className="flex items-center gap-2">
                <NsofarLogo size="sm" />
              </div>
              <button onClick={() => setSidebarOpen(false)} aria-label="Close menu"><X size={20} /></button>
            </div>
            <nav className="flex-1 overflow-y-auto p-4 space-y-1">
              {filteredNavItems.filter(item => {
                if (!item.featureKey) return true;
                const access = canAccess(item.featureKey);
                return access.reason !== 'feature_disabled' && access.reason !== 'disabled_for_user';
              }).map(item => {
                const active = currentPath === item.path;
                return (
                  <button key={item.path}
                    onClick={() => { navigate(item.path); setSidebarOpen(false); }}
                    className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-colors"
                    style={{ background: active ? 'var(--brand-primary-light)' : 'transparent', color: active ? 'var(--brand-primary)' : 'var(--text-muted)' }}>
                    <item.icon size={18} /> {item.label}
                    {item.isNew && isNewFeature(user?.created_at) && (
                      <span className="ml-auto px-1.5 py-0.5 rounded text-[11px] font-bold text-white" style={{ background: 'var(--brand-primary)' }}>New</span>
                    )}
                  </button>
                );
              })}
            </nav>
            <div className="p-4 border-t" style={{ borderColor: 'var(--border-color)' }}>
              <button data-testid="mobile-menu-logout-btn" onClick={logout} className="w-full flex items-center gap-2 px-3 py-2 text-sm" style={{ color: 'var(--error)' }}>
                <LogOut size={16} /> Sign Out
              </button>
            </div>
          </aside>
        </div>
      )}

      {/* Main content */}
      <div className="flex-1 lg:ml-64 flex flex-col min-h-screen min-w-0">
        {/* Top bar. It stays at the top of the screen while the page scrolls
            under it: no box above it may be a scroll container (see `.clip-x`
            in index.css). `app-top-bar` tells the page how much room to keep
            clear when it scrolls something to the top. */}
        <header className="app-top-bar sticky top-0 z-20 glass border-b px-4 lg:px-8 py-3 flex items-center gap-4" style={{ borderColor: 'var(--border-color)' }}>
          <button className="lg:hidden" onClick={() => setSidebarOpen(true)} data-testid="mobile-menu-btn" aria-label="Open menu">
            <Menu size={22} style={{ color: 'var(--text-main)' }} />
          </button>
          <div className="flex-1" />

          {/* Trust chip — sm+ only (mobile top bar is already packed) */}
          <TrustChip />

          {/* Dark-mode toggle — sm+ only, same reason */}
          <DarkModeToggle />

          {/* Currency Switcher */}
          <CurrencySwitcher compact />

          {/* Notification Bell */}
          <div className="relative" ref={notifRef}>
            <button data-testid="notification-bell-btn" onClick={() => { setNotifOpen(!notifOpen); if (!notifOpen) markAllRead(); }}
              aria-label={unreadCount > 0 ? `Notifications (${unreadCount} unread)` : 'Notifications'}
              aria-expanded={notifOpen}
              className="w-10 h-10 rounded-full flex items-center justify-center relative transition-colors"
              style={{ background: notifOpen ? 'var(--brand-primary-light)' : 'transparent' }}>
              {unreadCount > 0 ? <BellRing size={20} style={{ color: 'var(--brand-primary)' }} /> : <Bell size={20} style={{ color: 'var(--text-muted)' }} />}
              {unreadCount > 0 && (
                <span className="absolute -top-0.5 -right-0.5 w-5 h-5 rounded-full text-xs flex items-center justify-center text-white font-bold" style={{ background: 'var(--error)' }}
                  data-testid="notification-badge">
                  {unreadCount > 9 ? '9+' : unreadCount}
                </span>
              )}
            </button>

            {/* Notification Dropdown */}
            {notifOpen && (
              <div className="fixed left-2 right-2 sm:absolute sm:left-auto sm:right-0 top-16 sm:top-12 sm:w-96 max-h-[480px] nsofar-card shadow-lg overflow-hidden animate-fade-in-up z-50"
                data-testid="notification-dropdown">
                <div className="p-4 border-b flex items-center justify-between" style={{ borderColor: 'var(--border-color)' }}>
                  <h3 className="text-sm font-semibold" style={{ fontFamily: 'Outfit, sans-serif' }}>Notifications</h3>
                  <div className="flex items-center gap-2">
                    {notifPermission === 'unsupported' && (
                      <span className="text-xs px-2 py-0.5 rounded-full" style={{ background: 'var(--bg-main)', color: 'var(--text-muted)' }}>
                        Add to Home Screen for push
                      </span>
                    )}
                    {notifPermission === 'default' && (
                      <button className="text-xs px-2 py-1 rounded-full font-medium"
                        style={{ background: 'var(--brand-primary-light)', color: 'var(--brand-primary)' }}
                        onClick={requestNotifPermission} data-testid="enable-push-btn">
                        Enable Push
                      </button>
                    )}
                    {notifPermission === 'denied' && (
                      <span className="text-xs px-2 py-0.5 rounded-full" style={{ background: 'var(--error-light)', color: 'var(--error)' }}>
                        Push Blocked
                      </span>
                    )}
                    {notifPermission === 'granted' && (
                      <span className="text-xs px-2 py-0.5 rounded-full" style={{ background: 'var(--success-light)', color: 'var(--success)' }}>
                        Push On
                      </span>
                    )}
                  </div>
                </div>
                <div className="overflow-y-auto max-h-[380px]">
                  {notifications.length > 0 ? (
                    <div>
                      {notifications.map((n, i) => {
                        const ni = getNotifIcon(n.type);
                        const IconComp = ni.icon;
                        return (
                          <div key={n.id}
                            className="flex items-start gap-3 p-3 border-b nsofar-row-hover"
                            style={{ borderColor: 'var(--border-color)', opacity: n.read ? 0.7 : 1 }}
                            data-testid={`notification-item-${n.id}`}>
                            <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: ni.bg }}>
                              <IconComp size={14} style={{ color: ni.color }} />
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-xs font-semibold" style={{ color: 'var(--text-main)' }}>{n.title}</p>
                              <p className="text-xs leading-relaxed" style={{ color: 'var(--text-muted)' }}>{n.body}</p>
                              <div className="flex items-center gap-2 mt-1">
                                <span className="text-xs" style={{ color: 'var(--text-light)' }}>
                                  {new Date(n.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                </span>
                                {n.family_member_name && (
                                  <span className="text-xs px-1.5 py-0.5 rounded" style={{ background: 'var(--brand-primary-light)', color: 'var(--brand-primary)' }}>
                                    {n.family_member_name}
                                  </span>
                                )}
                              </div>
                              {/* docs/41 P2-3/P2-4: a proactive message carries exactly ONE
                                  suggested action — rendered as a chip into the assistant */}
                              {n.action_type === 'assistant_prompt' && n.assistant_prompt && (
                                <button
                                  className="mt-2 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium transition-all active:scale-95"
                                  style={{ background: 'var(--brand-primary)', color: 'white' }}
                                  onClick={() => { setNotifOpen(false); navigate(`/ai-chat?prompt=${encodeURIComponent(n.assistant_prompt)}`); }}
                                  data-testid={`notif-assistant-chip-${n.id}`}>
                                  <Sparkles size={11} /> {n.action_label || 'Ask nsofar'}
                                </button>
                              )}
                            </div>
                            <button className="p-1 rounded hover:bg-[var(--error-light)] flex-shrink-0" onClick={() => dismissNotif(n.id)} title="Dismiss">
                              <X size={12} style={{ color: 'var(--text-light)' }} />
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="p-8 text-center">
                      <Bell size={24} className="mx-auto mb-2" style={{ color: 'var(--text-light)' }} />
                      <p className="text-xs" style={{ color: 'var(--text-muted)' }}>No notifications</p>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

        </header>

        {/* `relative`: absolutely positioned children with no positioned parent
            of their own are anchored here. The entry animation used to do that
            by accident, and lets go when it ends (see .page-enter). */}
        <main
          className={`relative flex-1 p-4 lg:px-10 lg:py-8 overflow-x-hidden min-w-0 page-enter ${isUser ? 'pb-24 lg:pb-8' : ''}`}
          key={currentPath}
        >
          <ConsentBanner />
          <OpenConversationProvider value={setConversationOpen}>{children}</OpenConversationProvider>
        </main>
        <footer className={`disclaimer-footer ${isUser ? 'mb-20 lg:mb-0' : ''}`}>
          This is not medical, financial, or legal advice. Always consult qualified professionals.
        </footer>
      </div>

      {/* docs/41 P2-4: persistent assistant entry — the FAB is the "always
          reachable" door into the one brain. NRI portal only (elderly keeps
          its own big-button surface); hidden on the chat page itself, and
          below lg in an open /chat conversation; sits above the mobile
          bottom nav. */}
      {showAssistantFab && (
        <button
          type="button"
          onClick={() => navigate('/ai-chat')}
          aria-label="Ask nsofar assistant"
          data-testid="assistant-fab"
          data-tour="assistant"
          className="fixed z-40 right-4 bottom-24 lg:right-6 lg:bottom-20 w-14 h-14 rounded-full flex items-center justify-center transition-all active:scale-95 hover:shadow-xl"
          style={{ background: 'var(--brand-primary)', color: 'white', boxShadow: '0 6px 20px -4px rgba(0,0,0,0.35)' }}
        >
          <Sparkles size={22} />
        </button>
      )}

      {/* Mobile bottom nav (Session 7 / B1). User role only; hidden on lg+. */}
      {isUser && (
        <nav
          className="lg:hidden fixed bottom-0 left-0 right-0 z-40 bg-white border-t"
          style={{
            borderColor: 'var(--border-color)',
            paddingBottom: 'env(safe-area-inset-bottom)',
            boxShadow: '0 -2px 12px -2px rgba(0,0,0,0.06)',
          }}
          aria-label="Primary"
          data-testid="bottom-nav"
          data-tour="bottom-nav"
        >
          <div className="grid grid-cols-4">
            {bottomNavItems.filter(item => {
              if (!item.featureKey) return true;
              if (!loaded) return false;
              const access = canAccess(item.featureKey);
              if (!access || access.accessible !== false) return true;
              // Plans (showUnlessDisabled): hide only on an explicit disable.
              if (item.showUnlessDisabled) {
                return access.reason !== 'feature_disabled' && access.reason !== 'disabled_for_user';
              }
              return false;
            }).map(item => {
              const Icon = item.icon;
              const active = currentPath === item.path;
              return (
                <button
                  key={item.path}
                  type="button"
                  onClick={() => navigate(item.path)}
                  data-testid={`bottom-nav-${item.label.toLowerCase()}`}
                  aria-current={active ? 'page' : undefined}
                  aria-label={item.label}
                  className="relative flex flex-col items-center justify-center gap-1 py-2.5 transition-colors"
                  style={{ color: active ? 'var(--brand-primary)' : 'var(--text-muted)' }}
                >
                  {active && (
                    <span
                      className="absolute top-0 left-1/2 -translate-x-1/2 h-0.5 w-10 rounded-b-full"
                      style={{ background: 'var(--brand-primary)' }}
                      aria-hidden="true"
                    />
                  )}
                  <Icon size={22} strokeWidth={active ? 2.25 : 1.75} />
                  <span className="text-xs font-medium leading-none">{item.label}</span>
                </button>
              );
            })}
          </div>
        </nav>
      )}

      {/* Outside <main>: while <main>'s entry animation runs it is the
          containing block of anything `fixed` inside it, and this prompt can
          open in that time. Keyed like <main>, so it still re-asks on every
          page as it did when it was mounted in there. */}
      <LoginConsentPrompt key={currentPath} />

      {/* Below lg a family account has the bottom nav in the corner where the
          Feedback trigger floats for everyone else, and on most pages the
          assistant button above the nav: the trigger goes above both, and
          into the button's place where the button is left out. */}
      <FeedbackWidget place={showAssistantFab ? 'aboveAssistant' : isUser ? 'aboveNav' : 'corner'} />
    </div>
  );
}
